import { useCallback, useState } from 'react';
import {
  GIST_POST_MAX,
  GIST_REPORT_REASONS,
  type GistDecision,
  type GistReportReason,
  type GistReportView,
} from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { ReportedVoice } from '../lib/reported-voice';
import { GistPhotos, TeamPicture } from './gist-photos';
import { useRequest } from '../lib/use-request';

const reasonOf = (reason: string) => GIST_REPORT_REASONS[reason as GistReportReason] ?? reason;
const STATUS: Record<string, string> = {
  live: 'Showing',
  held: 'Held back',
  removed: 'Taken down',
  deleted: 'Deleted by its author',
};

// Gist for the team: photos waiting for a moderator; posts and comments players reported (enough reports hold one back until the team looks),
// removed for good or kept and shown again (a recent two-step check, audited); and London Gist news posts.
export function GistReportsPage() {
  const { can } = useAdmin();
  const [all, setAll] = useState(false);
  const reports = useRequest(useCallback(() => api.admin.gistReports(all), [all]));
  const news = useRequest(useCallback(() => api.admin.gistNews(), []));
  const [deciding, setDeciding] = useState<{ item: GistReportView; outcome: GistDecision['outcome'] } | null>(null);
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState('');
  const decide = async () => {
    if (!deciding) return;
    await api.admin.decideGistReport(deciding.item.id, {
      kind: deciding.item.kind,
      outcome: deciding.outcome,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setDone(deciding.outcome === 'remove' ? 'Taken down.' : 'Kept and showing.');
    await reports.reload();
  };
  const publish = async () => {
    await api.admin.postGistNews(text.trim(), `gist-news-${crypto.randomUUID()}`);
    setText('');
    setDone('News posted on London Gist.');
    await news.reload();
  };
  const list = reports.data?.reports ?? [];
  return (
    <section>
      <PageHeader
        title="Gist"
        description="Photos waiting for a check, posts and comments players reported on Gist, to take down or keep; and London Gist news posts."
      />
      {done && (
        <p role="status" className="admin-muted">
          {done}
        </p>
      )}
      <GistPhotos onDone={setDone} />
      <h2 className="content-subtitle">Reports</h2>
      <div className="admin-toolbar">
        <Button type="button" variant={all ? 'outline' : 'default'} onClick={() => setAll(false)}>
          Open{reports.data ? ` (${reports.data.open})` : ''}
        </Button>
        <Button type="button" variant={all ? 'default' : 'outline'} onClick={() => setAll(true)}>
          All reports
        </Button>
      </div>
      {reports.error && <ErrorNotice error={reports.error} onRetry={() => void reports.reload()} />}
      {reports.busy && !reports.data && <p className="admin-muted">Loading…</p>}
      {reports.data && list.length === 0 && (
        <div className="admin-empty">
          <strong className="admin-empty-title">{all ? 'No reports yet.' : 'Nothing to review.'}</strong>
        </div>
      )}
      <ul className="admin-reports">
        {list.map((item) => (
          <li key={item.id} className="admin-panel" data-testid="gist-report">
            <div className="admin-player-head">
              <h2>
                {item.kind === 'post' ? '📰 Post' : '💬 Comment'}{' '}
                <span className="admin-muted">by {item.author.name}</span>
              </h2>
              {item.decided ? (
                <Badge variant="secondary">{item.decided === 'removed' ? 'Taken down' : 'Kept'}</Badge>
              ) : (
                <Badge>Open</Badge>
              )}
            </div>
            <dl className="admin-facts">
              <dt>What it says</dt>
              <dd>
                <strong>{item.body || (item.sticker ? `A sticker (${item.sticker})` : '')}</strong>
                {item.photo && <TeamPicture src={item.photo.src} alt="The photo on the post" />}
                {item.voice &&
                  (item.voice.playPath ? (
                    <ReportedVoice path={item.voice.playPath} />
                  ) : (
                    <span className="admin-muted"> 🎤 A voice note (not kept)</span>
                  ))}
              </dd>
              <dt>Now</dt>
              <dd>{STATUS[item.status] ?? item.status}</dd>
              <dt>Posted</dt>
              <dd>{when(item.at)}</dd>
            </dl>
            <ol className="admin-chat-context" aria-label="The reports">
              {item.reports.map((line, index) => (
                <li key={`${line.at}-${index}`}>
                  <strong>{line.by}</strong> <span>{reasonOf(line.reason)}</span>{' '}
                  <small className="admin-muted">{when(line.at)}</small>
                </li>
              ))}
            </ol>
            {!item.decided && can('players.support') && (
              <div className="admin-page-actions wrap">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setNote('');
                    setDone(null);
                    setDeciding({ item, outcome: 'remove' });
                  }}
                >
                  Take down
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setNote('');
                    setDone(null);
                    setDeciding({ item, outcome: 'keep' });
                  }}
                >
                  Keep it
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      <h2 className="content-subtitle">London Gist news</h2>
      {can('content.write') && (
        <div className="admin-page-actions wrap">
          <Button type="button" onClick={() => setWriting(true)}>
            Post news
          </Button>
        </div>
      )}
      {news.error && <ErrorNotice error={news.error} onRetry={() => void news.reload()} />}
      <ul className="admin-reports" aria-label="News posts">
        {news.data?.news.map((post) => (
          <li key={post.id} className="admin-panel">
            <p>
              <strong>{post.body}</strong>
            </p>
            <small className="admin-muted">
              {when(post.at)} · ❤️ {post.likes} · 💬 {post.comments}
            </small>
          </li>
        ))}
      </ul>
      {news.data && news.data.news.length === 0 && <p className="admin-muted">No news posted yet.</p>}

      <FormDialog
        open={!!deciding}
        title={deciding?.outcome === 'remove' ? 'Take this down' : 'Keep this'}
        description={
          deciding?.outcome === 'remove'
            ? 'It comes down for good and its author is told.'
            : 'It shows again (if its author has not deleted it). The reports are closed.'
        }
        submitLabel={deciding?.outcome === 'remove' ? 'Take down' : 'Keep'}
        onSubmit={decide}
        onClose={() => setDeciding(null)}
      >
        <div className="admin-field">
          <Label htmlFor="gist-note">Note for the audit log (optional)</Label>
          <Textarea id="gist-note" maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
      </FormDialog>
      <FormDialog
        open={writing}
        title="Post London Gist news"
        description="It goes at the top of everyone's Latest, from 📰 London Gist."
        submitLabel="Post"
        onSubmit={publish}
        onClose={() => setWriting(false)}
      >
        <div className="admin-field">
          <Label htmlFor="gist-news">The news</Label>
          <Textarea
            id="gist-news"
            maxLength={GIST_POST_MAX}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          <small className="admin-muted">
            {text.trim().length}/{GIST_POST_MAX}
          </small>
        </div>
      </FormDialog>
    </section>
  );
}
